/**
 * SEARCH-002 §3.3 / §5 — Registre extensible des catégories de la palette ⌘K.
 *
 * Miroir front du registre backend (SEARCH-001 §4). Ajouter une catégorie =
 * 1 entrée ici (+1 contributeur back + 1 trigger SQL). Toute la navigation, le
 * gating RBAC, l'ordre stable et le routage « Voir tout » sont portés par ce
 * registre — aucune logique en dur ailleurs dans la palette.
 *
 * Icônes MONOCHROMES (text-muted-foreground) : le domaine se lit via la FORME
 * de l'icône + l'en-tête de groupe, jamais via une couleur (§2.2).
 *
 * HUB : ce module compose les types (`searchCategoriesTypes`) et le registre
 * (`searchCategoriesRegistry`) et les ré-exporte ; l'API publique reste
 * importable depuis `@/lib/searchCategories`.
 */

export {
    type SourceType,
    type SearchHit,
    type CategoryConfig,
} from './searchCategoriesTypes'

export { CATEGORY_REGISTRY, ORDERED_SOURCE_TYPES } from './searchCategoriesRegistry'

import { CATEGORY_REGISTRY, ORDERED_SOURCE_TYPES } from './searchCategoriesRegistry'
import type { SourceType } from './searchCategoriesTypes'

/**
 * Types que CET utilisateur peut demander à `GET /search/global` — jumeau front de
 * `allowed_types_for_permissions` (`backend/src/infra/search_repository/registry.py`).
 *
 * ⚠️ **SOURCE UNIQUE des deux surfaces de recherche** (palette ⌘K et sélecteur
 * « Relier une fiche »). Elles recopiaient la même ligne chacune de son côté ;
 * quand le registre a gagné les slugs alternatifs, une recopie aurait suivi et
 * l'autre non — et un défaut de droits ne se voit pas : l'écran affiche
 * simplement « aucun résultat ».
 *
 * Le paramètre `types` de la route ne peut que RESTREINDRE : ce qui n'est pas
 * demandé ici n'est jamais cherché, quoi qu'autorise le serveur.
 */
export function searchableTypesFor(hasPermission: (slug: string) => boolean): SourceType[] {
    return ORDERED_SOURCE_TYPES.filter((type) => {
        const cfg = CATEGORY_REGISTRY[type]
        return hasPermission(cfg.permission) || !!cfg.permissionAlt?.some(hasPermission)
    })
}

/**
 * Types « personne » : porteurs d'une identité humaine → avatar à initiales par
 * ligne (SEARCH-002 §A5). Les autres types n'affichent l'icône QUE sur l'en-tête
 * de groupe (pas de répétition par ligne).
 */
export const PERSON_SOURCE_TYPES: ReadonlySet<SourceType> = new Set<SourceType>([
    'contact',
    'learner',
    'user',
    'instructor',
])

export function isPersonType(type: SourceType): boolean {
    return PERSON_SOURCE_TYPES.has(type)
}

/**
 * Types proposés dans « Filtrer par » (§3) — jetons de scope à champ vide. Ordre
 * = priorité d'usage (les plus courants d'abord). Toujours INTERSECTÉ avec les
 * permissions de l'utilisateur côté palette (un type non autorisé n'apparaît
 * jamais). Restreint au sous-ensemble indexé pertinent (Sessions/Intervenants
 * s'ajouteront ici quand leurs tables seront indexées).
 */
// Ordre = celui du cycle de vie d'un OF : qui (client, apprenant, intervenant),
// quoi (formation), combien (devis), puis les surfaces de support.
// `instructor` et `quote` ajoutés le 2026-08-02 (retour PO) — leurs contributeurs
// d'index existaient déjà, seuls les filtres manquaient.
export const SCOPE_FILTER_TYPES: readonly SourceType[] = [
    'client',
    'learner',
    'instructor',
    'formation',
    'quote',
    'contact',
    'location',
    'document',
]

/** Initiales (1-2 lettres) d'un libellé, pour l'avatar des lignes « personne ». */
export function initialsFromLabel(label: string): string {
    const parts = label.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Signature minimale de `t` (i18next) acceptée par `humanizeMeta`. */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

/**
 * Humanise une valeur d'affichage de recherche (secondaire de ligne, valeur de
 * champ d'aperçu) pour **ne jamais montrer un code enum brut** (ex.
 * `NEEDS_ANALYSIS_DOCUMENT`). Garde-fou universel (résultats gauche + aperçu droite).
 *
 * Règle : seules les chaînes **purement enum** (`[A-Z0-9_]+` avec ≥ 1 lettre, tout
 * en majuscules) sont transformées — les vraies données (villes, emails, réfs
 * `FOR-0000002`, libellés accentués) passent **inchangées**. Traduction d'abord
 * (`globalSearch.meta.<CODE>`), sinon prettify (`NEEDS_ANALYSIS_DOCUMENT` →
 * « Needs analysis document ») pour tout code inconnu.
 */
export function humanizeMeta(value: string | null | undefined, t: TranslateFn): string {
    if (!value) return ''
    const looksEnum = /^[A-Z0-9_]+$/.test(value) && /[A-Z]/.test(value)
    if (!looksEnum) return value
    const translated = t(`globalSearch.meta.${value}`, { defaultValue: '' })
    if (translated) return translated
    const pretty = value.toLowerCase().replace(/_/g, ' ').trim()
    return pretty.charAt(0).toUpperCase() + pretty.slice(1)
}
